import { capitalize, chunk, clone, defer, throttle, uniqBy } from 'lodash'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { ContactsTable } from 'app/components/shared/ContactsTable'
import type { ContactJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { useWorkspace } from 'app/utils'
import { useQuery } from 'urql'
import { graphql } from 'app/generated/gql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

const PATIENT_ID_BATCH = 200

const PATIENT_IDS_QUERY = graphql(`
  query PatientIdsByReferralType($search: String, $pagination: PaginationInput) {
    patients(search: $search, pagination: $pagination) {
      hits {
        id
      }
      total
    }
  }
`)

export function ContactsByReferralType({
  referralType,
  count,
}: {
  referralType: string
  count?: number
}): ReactElement {
  const workspace = useWorkspace()
  const [page, setPage] = useState<number>(0)
  const [haveMore, setHaveMore] = useState<boolean>(true)
  const [sort, setSort] = useState<string>(``)
  const [query, setQuery] = useState<string>(``)
  const [filters, setFilters] = useState<string>(``)
  const [items, setItems] = useState<SearchResponse<ContactJson>>()
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [patientPage, setPatientPage] = useState(0)
  const [hasMorePatients, setHasMorePatients] = useState(true)
  const [patientPages, setPatientPages] = useState<string[][]>([])

  // Fetch patient IDs one page at a time
  const [{ data: patientData }] = useQuery({
    query: PATIENT_IDS_QUERY,
    variables: {
      search: `referralType:"${referralType.replace(/"/g, '\\"')}"`,
      pagination: { page: patientPage, limit: 1000 },
    },
    pause: !workspace || !hasMorePatients,
  })

  const patientTotal = patientData?.patients?.total ?? count ?? 0

  // Minimal side effect: store raw page results
  useEffect(() => {
    if (patientData?.patients?.hits && patientPages.length === patientPage) {
      setPatientPages((prev) => [...prev, patientData.patients.hits.map((p: { id: string }) => p.id)])
      setHasMorePatients((patientPage + 1) * 1000 < patientData.patients.total)
    }
  }, [patientData])

  // Pure computation: flatten all patient IDs
  const patientIds = useMemo(() => patientPages.flat(), [patientPages])

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event: any) => {
    let element = event.target
    if (!loadingContacts && haveMore && element.clientHeight + element.scrollTop >= element.scrollHeight - 400) {
      throttledSetPage()
    }
  }

  const throttledSetPage = throttle(() => setPage((prev) => prev + 1), 1000, { leading: true, trailing: false })

  const onFiltersChange = (filters: string) => {
    setHaveMore(true)
    setPage(0)
    setItems(undefined)
    setFilters(filters)
  }

  const onSearchValueChange = (searchValue: string) => {
    setHaveMore(true)
    setPage(0)
    setItems(undefined)
    setQuery(searchValue)
  }

  const onSortChange = (sort: string) => {
    setHaveMore(true)
    setPage(0)
    setSort(sort)
    defer(() => setItems(undefined))
  }

  // Fetch contacts linked to current patient IDs
  useEffect(() => {
    if (patientIds.length === 0 || haveMore === false) return
    bg(async () => {
      setLoadingContacts(true)
      try {
        const batches = chunk(patientIds, PATIENT_ID_BATCH)
        const batchPage = page < batches.length ? page : batches.length - 1
        const ids = batches[batchPage]
        if (!ids?.length) {
          setHaveMore(false)
          setLoadingContacts(false)
          return
        }

        const patientSearch = `patientIds:(${ids.join(' OR ')})`
        const searchParts = [patientSearch]
        if (query) searchParts.push(query)
        if (filters) searchParts.push(filters)

        const response = await gqlFetchList<ContactJson>(`contacts`, {
          search: searchParts.join(` AND `),
          pagination: { limit: 1000 },
          sort: sort ? { field: sort.replace(/^-/, ``), direction: sort.startsWith(`-`) ? `DESC` : `ASC` } : undefined,
        })

        setHaveMore(batchPage + 1 < batches.length)
        if (batchPage + 1 >= batches.length && hasMorePatients) {
          setPatientPage((prev) => prev + 1)
        }
        setItems((prev) => {
          const displayTotal = count ?? patientTotal
          if (!prev) return { ...response, total: displayTotal } as SearchResponse<ContactJson>
          return {
            page: batchPage,
            total: displayTotal,
            hits: clone(uniqBy([...prev.hits, ...(response.hits ?? [])], `id`)),
          } as SearchResponse<ContactJson>
        })
      } catch (err) {
        logError(`contacts`, `error`, err)
      } finally {
        setLoadingContacts(false)
      }
    })
  }, [patientIds, page, sort, query, filters])

  if (!workspace) return <></>
  return (
    <ContactsTable
      title={capitalize(referralType)}
      items={patientIds.length === 0 && !items ? undefined : ((items?.hits ?? []) as ContactJson[])}
      total={items?.total ?? count ?? patientTotal}
      scrollHandler={scrollHandler}
      filtersHandler={onFiltersChange}
      searchValueHandler={onSearchValueChange}
      sortHandler={onSortChange}
      enableDeepLinking={false}
      isManuallySorted
      activeSubpage="-contacts-by-referral-type"
    />
  )
}
